/* eslint react-hooks/immutability: ["warn"] */
/* eslint react-hooks/set-state-in-effect: ["warn"] */
import { useEffect, useState, useCallback } from "react";
import Linkbar from "@coworker/apprestructured/src/shared/components/Linkbar/Linkbar";
import { useTranslation } from "@coworker/locales";
import FullScreenPopup from "@coworker/apprestructured/src/layout/components/FullScreenPopup/FullScreenPopup";
import { Media } from "../types/media";
import { MediaSettingsForm } from "../Organisms/MediaSettingsForm";
import { FullWidthButton } from "../../SkapaButton";
import { useQueryClient } from "@tanstack/react-query";
import { QueryKeys } from "../hooks/queryKeys";
import { Image } from "../types/images";
import { updateImage } from "../services/images.service";
import { useAddImageToMedia } from "../hooks/useAddImageToMedia";
import { useMediasByStoreId } from "../hooks/useMediasByStoreId";
import { useParams } from "react-router";
import { useMediaById } from "../hooks/useMediaById";
import { getRequiredFields, getMediaTypeText } from "../tools";
import {
  checkEmptyMediaName,
  checkMediaName,
} from "../Organisms/Settings/SettingsForm.helper";
import useFixaNavigate from "@coworker/apprestructured/src/shared/wrappers/FixaNavigate/useFixaNavigate";
import { useImagesByMediaId } from "../hooks/useImagesByMediaId";
import { useUpdateMedia } from "../hooks/useUpdateMedia";
import { MessageModal } from "../Organisms/Modals/MessageModal";
import { OkCancelModal } from "../Organisms/Modals/OkCancelModal";
import { MediaTypes } from "../constants";
import { CenterContainerWithGap } from "../styles/styles";
import { ChangeMediaTypeConfirmationDialog } from "../Organisms/Modals/ChangeMediaTypeConfirmationModal";
import { useToastNotification } from "@coworker/components/src/components/ToastNotification";
import { useDeleteImages } from "../hooks/useDeleteImages";

export function UpdateMediaView() {
  const { t } = useTranslation();

  const { mediaId } = useParams();
  const navigate = useFixaNavigate();

  const { data: mediaResponse } = useMediaById(mediaId);
  const updateMediaMutation = useUpdateMedia(mediaId || "");
  const [showModalMessage, setShowModalMessage] = useState("");
  const [showConfirmCloseModal, setShowConfirmCloseModal] = useState(false);
  const [showChangeMediaTypeModal, setShowChangeMediaTypeModal] =
    useState(false);
  const [imagesAreUploading, setImagesAreUploading] = useState(false);
  const [currentMedia, setCurrentMedia] = useState({} as Media);
  const [currentCheckedMediaType, setCurrentCheckedMediaType] =
    useState<MediaTypes>(MediaTypes.NONE);
  const [originalMediaType, setOriginalMediaType] = useState(MediaTypes.NONE);

  const { data: mediasResponse } = useMediasByStoreId(currentMedia.storeId);
  const queryClient = useQueryClient();
  const { data: images } = useImagesByMediaId(currentMedia.id);
  const addImageToMedia = useAddImageToMedia(currentMedia.id);
  const deleteImageMutation = useDeleteImages();
  const { showToast } = useToastNotification();
  const [currentImages, setCurrentImages] = useState<Image[]>([]);
  const [removedImages, setRemovedImages] = useState<Image[]>([]);

  useEffect(() => {
    if (images && currentImages.length === 0) {
      setCurrentImages(images);
    }
  }, [images, currentImages]);

  const linkBarItems = [
    {
      label: t("mediaMaintenanceString"),
      to: "/mediamaintenance",
    },
  ];
  if (mediaResponse) {
    if (mediaResponse.parent) {
      linkBarItems.push({
        label: mediaResponse.parent.name,
        to: `/mediamaintenance/tabs/${mediaResponse.parent.id}`,
      });
    }
    linkBarItems.push({
      label: mediaResponse?.media.name ?? t("mediaNameString"),
      to: `/mediamaintenance/tabs/${mediaId}`,
    });
  }

  const linkbar = (
    <Linkbar currentLabel={t("updateMediaString")} items={linkBarItems} />
  );

  useEffect(() => {
    if (mediaResponse) {
      setCurrentMedia(mediaResponse.media);
      setCurrentCheckedMediaType(mediaResponse.media.mediaType as MediaTypes);
      setOriginalMediaType(mediaResponse.media.mediaType as MediaTypes);
    }
  }, [mediaResponse]);

  const onImageAddStart = () => {
    setImagesAreUploading(true);
  };

  const onImageAddEnd = async (images: Image[]) => {
    setCurrentImages((prevImages) => [...prevImages, ...images]);
    setImagesAreUploading(false);
  };

  const onImageRemove = async (images: Image[]) => {
    for (const image of images) {
      if (
        currentImages.find((existingimage) => existingimage.id === image.id)
      ) {
        setRemovedImages((prevImages) => [...prevImages, image]);

        currentImages.splice(
          currentImages.findIndex(
            (existingimage) => existingimage.id === image.id
          ),
          1
        );
      }
    }
  };

  const onImageUpdate = async (image: Image) => {
    if (image.id) {
      try {
        await updateImage(image.id, image);
        await queryClient.invalidateQueries({
          queryKey: [QueryKeys.ImagesByMedia],
        });
      } catch {
        alert(t("searchErrorString"));
      }
    }
  };

  function validateMediaFields(media: Media): boolean {
    const requiredKeys = getRequiredFields(media);
    return requiredKeys.every((key) => {
      const value = media[key as keyof Media];
      return value;
    });
  }

  const [showErrorMessage, setshowErrorMessage] = useState(true);

  const ValidateMedia = useCallback(() => {
    const isDisabled =
      checkEmptyMediaName(currentMedia) ||
      checkMediaName(currentMedia, mediasResponse?.medias || []) ||
      !validateMediaFields(currentMedia);

    setshowErrorMessage(isDisabled);
  }, [currentMedia, mediasResponse]);

  useEffect(() => {
    ValidateMedia();
  }, [ValidateMedia]);

  const updateMediaImages = async () => {
    const mediaId = currentMedia.id;
    if (!mediaId) {
      alert(t("searchErrorString"));
      return;
    }

    for (const image of removedImages) {
      if (image.id) {
        await deleteImageMutation.mutateAsync({
          imageIds: [image.id],
          mediaId,
        });
      }
    }

    if (images && currentImages.length === 0 && images.length === 0) return;

    if (images && images.length > 0) {
      for (const image of currentImages) {
        if (!images.find((existingimage) => existingimage.id === image.id)) {
          await addImageToMedia.mutateAsync({ ...image, mediaId });
        }
      }
      return;
    }

    for (const image of currentImages) {
      await addImageToMedia.mutateAsync({ ...image, mediaId });
    }
  };

  const submitDisabled = imagesAreUploading;

  const onSubmit = async () => {
    if (showErrorMessage) return;
    try {
      await updateMediaImages();
      await updateMediaMutation.mutateAsync(currentMedia);
      showToast(
        t("nameWasUpdatedString", {
          name: getMediaTypeText(currentMedia.mediaType as MediaTypes),
        })
      );
      navigate(`/mediamaintenance/tabs/${mediaId}`);
    } catch (error) {
      console.log(error);
      setShowModalMessage(t("couldNotUpdateRoomTryAgainString"));
    }
  };

  if (!currentMedia) {
    return <></>;
  }

  return (
    <FullScreenPopup
      noPadding
      linkbar={linkbar}
      fullHeightContent={true}
      fieldsAreEmpty={showErrorMessage}
      actionBarContent={
        <CenterContainerWithGap $centerText={true}>
          <FullWidthButton
            type="secondary"
            text={t("cancelString")}
            onClick={() => {
              if (!showErrorMessage) {
                setShowConfirmCloseModal(true);
              } else {
                navigate(`/mediamaintenance/tabs/${mediaId}`);
              }
            }}
          />
          <FullWidthButton
            type="primary"
            text={t("saveString")}
            onClick={() => {
              if (
                originalMediaType !== currentMedia.mediaType &&
                !showErrorMessage
              ) {
                setShowChangeMediaTypeModal(true);
              } else {
                onSubmit();
              }
            }}
            loading={showChangeMediaTypeModal}
            disabled={submitDisabled}
          />
        </CenterContainerWithGap>
      }
    >
      <MediaSettingsForm
        currentMedia={currentMedia}
        medias={mediasResponse?.medias || []}
        setCurrentMedia={(newMedia) => {
          setCurrentMedia(newMedia);
        }}
        isUploadingImages={imagesAreUploading}
        currentCheckedMediaType={currentCheckedMediaType}
        setCurrentCheckedMediaType={setCurrentCheckedMediaType}
        images={currentImages}
        onImageAddStart={onImageAddStart}
        onImageAddEnd={onImageAddEnd}
        onImageRemove={onImageRemove}
        onImageUpdate={onImageUpdate}
        isUpdate={true}
      />
      {!!showModalMessage && (
        <MessageModal
          message={showModalMessage}
          closeModal={() => {
            setShowModalMessage("");
          }}
        />
      )}
      {showConfirmCloseModal && (
        <OkCancelModal
          onOk={() => {
            setShowConfirmCloseModal(false);
            navigate(`/mediamaintenance/tabs/${mediaId}`);
            setCurrentMedia(mediaResponse?.media ?? ({} as Media));
            queryClient.invalidateQueries({
              queryKey: [QueryKeys.ImagesByMedia, mediaId],
            });
          }}
          onCancel={() => {
            setShowConfirmCloseModal(false);
          }}
          title={t("confirmCloseString")}
          text={t("allChangesLostCloseString")}
        />
      )}
      {showChangeMediaTypeModal && (
        <ChangeMediaTypeConfirmationDialog
          onConfirm={async () => {
            await onSubmit();
            setShowChangeMediaTypeModal(false);
          }}
          onCancel={() => {
            setShowChangeMediaTypeModal(false);
          }}
          media={currentMedia}
          originalMediaType={originalMediaType}
        />
      )}
    </FullScreenPopup>
  );
}
